/**
 * Multi-Language Translations for KuberCrypto Ramp Widget
 * Supports: English (en), Hindi (hi), French (fr), Spanish (sp/es), Chinese (ch/zh)
 */

export const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'hi', name: 'हिंदी', flag: '🇮🇳' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'sp', name: 'Español', flag: '🇪🇸' },
  { code: 'ch', name: '中文', flag: '🇨🇳' },
];

/**
 * Normalize language parameter to supported language code: 'en' | 'hi' | 'fr' | 'sp' | 'ch'
 * @param {string} langParam
 * @returns {'en' | 'hi' | 'fr' | 'sp' | 'ch'}
 */
export function normalizeLanguage(langParam) {
  if (!langParam) return 'en';
  const l = String(langParam).toLowerCase().trim();
  if (l === 'hi' || l === 'hindi') return 'hi';
  if (l === 'fr' || l === 'french') return 'fr';
  if (l === 'sp' || l === 'es' || l === 'spanish') return 'sp';
  if (l === 'ch' || l === 'zh' || l === 'chinese') return 'ch';
  return 'en';
}

export const TRANSLATIONS = {
  en: {
    buyCrypto: 'Buy Crypto',
    sellCrypto: 'Sell Crypto',
    buy: 'Buy',
    sell: 'Sell',
    refreshQuotes: 'Refresh quotes',
    youSpend: 'You spend',
    youSell: 'You sell',
    youReceive: 'You receive',
    youReceiveEst: 'You receive (est.)',
    network: 'Network',
    change: 'Change',
    selectToken: 'Select Token',
    selectNetwork: 'Select Network',
    searchToken: 'Search token...',
    noTokensAvailable: 'No tokens available for {fiat}',
    tokenAvailableNetworks: '{symbol} is available on {count} networks',
    payUsing: 'Pay using',
    payoutMethod: 'Payout method',
    selectPaymentMethod: 'Select payment method',
    recommended: 'Recommended',
    bestRate: 'Best Rate',
    selectRouteProvider: 'Select Route Provider',
    realtimeComparison: 'Real-time Comparison',
    routesFound: '{count} routes found for {amount} {from} → {to}',
    walletAddress: 'Wallet Address',
    connectWalletToContinue: 'Connect Wallet to Continue',
    enterValidWalletAddress: 'Enter a Valid Wallet Address',
    enterValidNetworkAddress: 'Enter a valid {network} address',
    disclaimerPart1: 'I understand that I will be ',
    disclaimerPart2: ' cryptocurrency directly through 3rd party service providers. KuberCrypto is an aggregation platform; hence, will not take responsibility for any issues that may affect my transactions through the 3rd party service providers.',
    selling: 'selling',
    purchasing: 'purchasing',
    acceptDisclaimerToContinue: 'Accept Disclaimer to Continue',
    buyCurrency: 'Buy {crypto}',
    sellCurrency: 'Sell {crypto}',
    processing: 'Processing...',
    quotesOutdated: 'Quotes may be outdated.',
    refresh: 'Refresh',
    paymentInProgress: 'Payment in Progress',
    waitingConfirmation: 'Waiting for payment confirmation...',
    openPaymentPage: 'Open {provider} Payment Page',
    paymentCompleted: 'Payment Completed Successfully!',
    redirecting: 'Redirecting...',
    return: 'Return',
  },
  hi: {
    buyCrypto: 'क्रिप्टो खरीदें',
    sellCrypto: 'क्रिप्टो बेचें',
    buy: 'खरीदें',
    sell: 'बेचें',
    refreshQuotes: 'कोट्स रिफ्रेश करें',
    youSpend: 'आप खर्च करेंगे',
    youSell: 'आप बेचेंगे',
    youReceive: 'आप प्राप्त करेंगे',
    youReceiveEst: 'आप प्राप्त करेंगे (अनुमानित)',
    network: 'नेटवर्क',
    change: 'बदलें',
    selectToken: 'टोकन चुनें',
    selectNetwork: 'नेटवर्क चुनें',
    searchToken: 'टोकन खोजें...',
    noTokensAvailable: '{fiat} के लिए कोई टोकन उपलब्ध नहीं है',
    tokenAvailableNetworks: '{symbol} {count} नेटवर्क पर उपलब्ध है',
    payUsing: 'भुगतान का माध्यम',
    payoutMethod: 'प्राप्ति का माध्यम',
    selectPaymentMethod: 'भुगतान विधि चुनें',
    recommended: 'अनुशंसित',
    bestRate: 'सर्वश्रेष्ठ दर',
    selectRouteProvider: 'रूट प्रदाता चुनें',
    realtimeComparison: 'रीयल-टाइम तुलना',
    routesFound: '{amount} {from} → {to} के लिए {count} रूट मिले',
    walletAddress: 'वॉलेट पता',
    connectWalletToContinue: 'जारी रखने के लिए वॉलेट कनेक्ट करें',
    enterValidWalletAddress: 'एक मान्य वॉलेट पता दर्ज करें',
    enterValidNetworkAddress: 'एक मान्य {network} पता दर्ज करें',
    disclaimerPart1: 'मैं समझता/समझती हूँ कि मैं 3rd पार्टी सेवा प्रदाताओं के माध्यम से सीधे क्रिप्टोकरेंसी ',
    disclaimerPart2: ' कर रहा/रही हूँ। KuberCrypto एक एग्रीगेशन प्लेटफॉर्म है; इसलिए, 3rd पार्टी सेवा प्रदाताओं के माध्यम से लेन-देन को प्रभावित करने वाले किसी भी मुद्दे की ज़िम्मेदारी नहीं लेगा।',
    selling: 'बेच',
    purchasing: 'खरीद',
    acceptDisclaimerToContinue: 'जारी रखने के लिए अस्वीकरण स्वीकार करें',
    buyCurrency: '{crypto} खरीदें',
    sellCurrency: '{crypto} बेचें',
    processing: 'प्रक्रिया जारी है...',
    quotesOutdated: 'कोट्स पुराने हो सकते हैं।',
    refresh: 'रिफ्रेश',
    paymentInProgress: 'भुगतान प्रगति पर है',
    waitingConfirmation: 'भुगतान पुष्टि की प्रतीक्षा है...',
    openPaymentPage: '{provider} भुगतान पृष्ठ खोलें',
    paymentCompleted: 'भुगतान सफलतापूर्वक पूर्ण हुआ!',
    redirecting: 'पुनर्निर्देशित किया जा रहा है...',
    return: 'वापस लौटें',
  },
  fr: {
    buyCrypto: 'Acheter de la crypto',
    sellCrypto: 'Vendre de la crypto',
    buy: 'Acheter',
    sell: 'Vendre',
    refreshQuotes: 'Actualiser les devis',
    youSpend: 'Vous dépensez',
    youSell: 'Vous vendez',
    youReceive: 'Vous recevez',
    youReceiveEst: 'Vous recevez (est.)',
    network: 'Réseau',
    change: 'Modifier',
    selectToken: 'Sélectionner un jeton',
    selectNetwork: 'Sélectionner un réseau',
    searchToken: 'Rechercher un jeton...',
    noTokensAvailable: 'Aucun jeton disponible pour {fiat}',
    tokenAvailableNetworks: '{symbol} est disponible sur {count} réseaux',
    payUsing: 'Payer avec',
    payoutMethod: 'Mode de paiement',
    selectPaymentMethod: 'Sélectionner le mode de paiement',
    recommended: 'Recommandé',
    bestRate: 'Meilleur taux',
    selectRouteProvider: 'Sélectionner le fournisseur',
    realtimeComparison: 'Comparaison en temps réel',
    routesFound: '{count} itinéraires trouvés pour {amount} {from} → {to}',
    walletAddress: 'Adresse du portefeuille',
    connectWalletToContinue: 'Connecter le portefeuille pour continuer',
    enterValidWalletAddress: 'Entrer une adresse de portefeuille valide',
    enterValidNetworkAddress: 'Entrer une adresse {network} valide',
    disclaimerPart1: 'Je comprends que je vais ',
    disclaimerPart2: ' des cryptomonnaies directement auprès de prestataires tiers. KuberCrypto est une plateforme d agrégation ; elle ne sera donc pas responsable des problèmes pouvant affecter mes transactions via les prestataires tiers.',
    selling: 'vendre',
    purchasing: 'acheter',
    acceptDisclaimerToContinue: 'Accepter la clause de non-responsabilité pour continuer',
    buyCurrency: 'Acheter {crypto}',
    sellCurrency: 'Vendre {crypto}',
    processing: 'Traitement en cours...',
    quotesOutdated: 'Les devis peuvent être obsolètes.',
    refresh: 'Actualiser',
    paymentInProgress: 'Paiement en cours',
    waitingConfirmation: 'En attente de la confirmation du paiement...',
    openPaymentPage: 'Ouvrir la page de paiement {provider}',
    paymentCompleted: 'Paiement terminé avec succès !',
    redirecting: 'Redirection...',
    return: 'Retour',
  },
  sp: {
    buyCrypto: 'Comprar Cripto',
    sellCrypto: 'Vender Cripto',
    buy: 'Comprar',
    sell: 'Vender',
    refreshQuotes: 'Actualizar cotizaciones',
    youSpend: 'Tú gastas',
    youSell: 'Tú vendes',
    youReceive: 'Tú recibes',
    youReceiveEst: 'Tú recibes (est.)',
    network: 'Red',
    change: 'Cambiar',
    selectToken: 'Seleccionar Token',
    selectNetwork: 'Seleccionar Red',
    searchToken: 'Buscar token...',
    noTokensAvailable: 'No hay tokens disponibles para {fiat}',
    tokenAvailableNetworks: '{symbol} está disponible en {count} redes',
    payUsing: 'Pagar con',
    payoutMethod: 'Método de pago',
    selectPaymentMethod: 'Seleccionar método de pago',
    recommended: 'Recomendado',
    bestRate: 'Mejor tasa',
    selectRouteProvider: 'Seleccionar Proveedor',
    realtimeComparison: 'Comparación en tiempo real',
    routesFound: '{count} rutas encontradas para {amount} {from} → {to}',
    walletAddress: 'Dirección de Wallet',
    connectWalletToContinue: 'Conecta la billetera para continuar',
    enterValidWalletAddress: 'Ingresa una dirección de wallet válida',
    enterValidNetworkAddress: 'Ingresa una dirección {network} válida',
    disclaimerPart1: 'Entiendo que estaré ',
    disclaimerPart2: ' criptomonedas directamente a través de proveedores externos. KuberCrypto es una plataforma de agregación; por lo tanto, no se responsabilizará por problemas que afecten mis transacciones con terceros.',
    selling: 'vendiendo',
    purchasing: 'comprando',
    acceptDisclaimerToContinue: 'Acepta el descargo para continuar',
    buyCurrency: 'Comprar {crypto}',
    sellCurrency: 'Vender {crypto}',
    processing: 'Procesando...',
    quotesOutdated: 'Las cotizaciones pueden estar desactualizadas.',
    refresh: 'Actualizar',
    paymentInProgress: 'Pago en curso',
    waitingConfirmation: 'Esperando confirmación del pago...',
    openPaymentPage: 'Abrir página de pago de {provider}',
    paymentCompleted: '¡Pago completado con éxito!',
    redirecting: 'Redirigiendo...',
    return: 'Volver',
  },
  ch: {
    buyCrypto: '购买加密货币',
    sellCrypto: '出售加密货币',
    buy: '购买',
    sell: '出售',
    refreshQuotes: '刷新报价',
    youSpend: '您支付',
    youSell: '您出售',
    youReceive: '您获得',
    youReceiveEst: '预计获得',
    network: '网络',
    change: '更改',
    selectToken: '选择代币',
    selectNetwork: '选择网络',
    searchToken: '搜索代币...',
    noTokensAvailable: '{fiat} 暂无可用代币',
    tokenAvailableNetworks: '{symbol} 支持 {count} 个网络',
    payUsing: '支付方式',
    payoutMethod: '收款方式',
    selectPaymentMethod: '选择支付方式',
    recommended: '推荐',
    bestRate: '最优汇率',
    selectRouteProvider: '选择路由服务商',
    realtimeComparison: '实时比对',
    routesFound: '已找到 {count} 条路线: {amount} {from} → {to}',
    walletAddress: '钱包地址',
    connectWalletToContinue: '连接钱包以继续',
    enterValidWalletAddress: '请输入有效的钱包地址',
    enterValidNetworkAddress: '请输入有效的 {network} 地址',
    disclaimerPart1: '我了解我将通过第三方服务商直接',
    disclaimerPart2: '加密货币。KuberCrypto 是一个聚合平台，因此对通过第三方服务商进行的交易出现的任何问题不承担责任。',
    selling: '出售',
    purchasing: '购买',
    acceptDisclaimerToContinue: '接受免责声明以继续',
    buyCurrency: '购买 {crypto}',
    sellCurrency: '出售 {crypto}',
    processing: '处理中...',
    quotesOutdated: '报价可能已过期。',
    refresh: '刷新',
    paymentInProgress: '支付处理中',
    waitingConfirmation: '等待付款确认...',
    openPaymentPage: '打开 {provider} 支付页面',
    paymentCompleted: '支付成功！',
    redirecting: '跳转中...',
    return: '返回',
  },
};

/**
 * Get translated text for given key and language with optional parameter substitution
 * @param {string} lang - Language code ('en', 'hi', 'fr', 'sp', 'ch')
 * @param {string} key - Translation key
 * @param {Object} [params] - Key-value replacements for {placeholder}
 * @returns {string} Translated string
 */
export function getTranslation(lang, key, params = {}) {
  const code = normalizeLanguage(lang);
  const dict = TRANSLATIONS[code] || TRANSLATIONS.en;
  let text = dict[key] || TRANSLATIONS.en[key] || key;

  if (params && typeof params === 'object') {
    Object.keys(params).forEach((paramKey) => {
      text = text.replace(new RegExp(`\\{${paramKey}\\}`, 'g'), params[paramKey]);
    });
  }

  return text;
}

export default getTranslation;
